import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import {
  SupportTaskProcessor,
  SupportTaskStatus,
  SupportTaskKind,
} from '@duke-hq/svc-api-db/client';
import { CheckCircle2, Calendar, Clock, X, type LucideIcon } from 'lucide-react';
import { SYDNEY_TZ, NYB_TASK_KINDS } from './constants';
import { calculateDayNumber } from './dateUtils';

export interface TaskStatusDisplay {
  icon: LucideIcon;
  color?: string;
  label: string;
  text: string;
  showDayNumber: boolean;
  dayNumber: number | null;
}

interface GetTaskStatusDisplayParams {
  status: SupportTaskStatus;
  processor: SupportTaskProcessor;
  kind: SupportTaskKind;
  processAnytimeAfter: Date;
  completedAt: Date | null;
  enrolmentCreatedAt?: Date;
}

export function getTaskStatusDisplay(params: GetTaskStatusDisplayParams): TaskStatusDisplay {
  const { status, processor, kind, processAnytimeAfter, completedAt, enrolmentCreatedAt } = params;

  const now = new Date();
  const scheduledDate = djs(processAnytimeAfter).tz(SYDNEY_TZ);
  const isPastScheduledTime = djs(processAnytimeAfter).isBefore(now);
  const isAutomated = processor === SupportTaskProcessor.AUTOMATED;
  const isCompleted = status === SupportTaskStatus.COMPLETED;
  const isCancelled = status === SupportTaskStatus.CANCELLED;
  const isSkipped = status === SupportTaskStatus.SKIPPED;
  const isFailed = status === SupportTaskStatus.FAILED;
  const isPending = status === SupportTaskStatus.PENDING;
  const isInProgress = status === SupportTaskStatus.IN_PROGRESS;

  const isNYBTask = NYB_TASK_KINDS.includes(kind);
  const dayNumber =
    enrolmentCreatedAt != null ? calculateDayNumber(enrolmentCreatedAt, processAnytimeAfter) : null;

  let icon: TaskStatusDisplay['icon'];
  let color: string | undefined;
  let label: string;
  let text: string;
  let showDayNumber = false;

  if (isCompleted) {
    icon = CheckCircle2;
    color = 'green.500';
    label = t`Completed:`;
    text = completedAt
      ? djs(completedAt).tz(SYDNEY_TZ).format('DD MMM YYYY')
      : scheduledDate.format('DD MMM YYYY');
  } else if (isCancelled) {
    icon = X;
    color = 'gray.500';
    label = t`Status:`;
    text = t`Cancelled`;
  } else if (isSkipped) {
    icon = X;
    color = 'gray.500';
    label = t`Status:`;
    text = t`Skipped`;
  } else if (isFailed) {
    icon = X;
    color = 'red.500';
    label = t`Status:`;
    text = t`Failed`;
  } else if (isAutomated) {
    if (isPastScheduledTime && (isPending || isInProgress)) {
      icon = Clock;
      color = 'blue.500';
      label = t`Status:`;
      text = t`Processing automatically`;
      showDayNumber = isNYBTask;
    } else {
      icon = Calendar;
      color = undefined;
      label = t`Scheduled for:`;
      text = scheduledDate.format('DD MMM YYYY');
      showDayNumber = isNYBTask;
    }
  } else {
    if (isPastScheduledTime && (isPending || isInProgress)) {
      icon = CheckCircle2;
      color = 'green.500';
      label = t`Status:`;
      text = t`Ready to action`;
      showDayNumber = isNYBTask;
    } else {
      icon = Calendar;
      color = undefined;
      label = t`Scheduled for:`;
      text = scheduledDate.format('DD MMM YYYY');
      showDayNumber = isNYBTask;
    }
  }

  return {
    icon,
    color,
    label,
    text,
    showDayNumber,
    dayNumber,
  };
}
